/**
 * Stubbed "extension side" of the EMR search protocol.
 *
 * In production the browser extension's content script answers
 * `STLMC_SEARCH_PATIENTS` requests by querying the real EMR (Practice Fusion).
 * That handler only runs on the EHR page, so for local/standalone development
 * this module stands in for it: it listens for outgoing requests and posts back
 * a canned `_response`, exercising the exact same postMessage round-trip.
 *
 * It only ever sees the request when the app is NOT actually nested in the
 * extension iframe (i.e. `window.parent === window`, the dev/standalone case).
 * When a real extension is present it owns `window.parent` and this listener
 * never receives the request — so leaving the stub installed is harmless.
 */

const MESSAGE_SOURCE = '__STLMC_MESSAGES__';
const RESPONSE_DELAY_MS = 400;

let installed = false;

// Fake EMR directory. emrIntegrationId is a GUID to match the ehrId format.
const STUB_EMR_PATIENTS = [
  { firstName: 'Robert', lastName: 'Lopez', dateOfBirth: '1975-03-08', emrIntegrationId: 'a1f4c2e0-7b3d-4c91-9a2e-1f6b8d4e5c70' },
  { firstName: 'Michael', lastName: 'Chen', dateOfBirth: '1984-02-14', emrIntegrationId: 'b2e5d3f1-8c4e-4a02-8b3f-2a7c9e5f6d81' },
  { firstName: 'James', lastName: 'Smith', dateOfBirth: '1986-04-12', emrIntegrationId: 'c3f6e4a2-9d5f-4b13-9c4a-3b8d0f6a7e92' },
  { firstName: 'David', lastName: 'Nguyen', dateOfBirth: '1979-09-21', emrIntegrationId: 'd4a7f5b3-ae60-4c24-8d5b-4c9e1a7b8f03' },
  { firstName: 'William', lastName: 'Johnson', dateOfBirth: '1968-12-02', emrIntegrationId: 'e5b8a6c4-bf71-4d35-9e6c-5d0f2b8c9a14' },
  { firstName: 'Carlos', lastName: 'Reyes', dateOfBirth: '1982-01-05', emrIntegrationId: 'f6c9b7d5-c082-4e46-8f7d-6e1a3c9d0b25' },
  { firstName: 'Daniel', lastName: 'Foster', dateOfBirth: '1973-05-27', emrIntegrationId: '07d0c8e6-d193-4f57-9a8e-7f2b4d0e1c36' }
];

// Fake medication list, keyed by emrIntegrationId. Each includes a Testosterone
// entry so the TRT cards can derive dose/refill during standalone dev.
function stubMedications(emrIntegrationId) {
  return [
    {
      emrMedicationId: `${emrIntegrationId}-med-1`,
      drugName: 'Testosterone Cypionate 200 MG/ML Intramuscular Solution',
      sig: '0.5cc IM once weekly',
      startDate: '2026-01-15',
      stopDate: '2026-07-15',
      isActive: true,
      isControlledSubstance: true
    },
    {
      emrMedicationId: `${emrIntegrationId}-med-2`,
      drugName: 'Anastrozole 1 MG Oral Tablet',
      sig: 'Take 1 tablet twice weekly',
      startDate: '2026-01-15',
      stopDate: null,
      isActive: true,
      isControlledSubstance: false
    }
  ];
}

// Fake document list, keyed by emrIntegrationId. Includes a qADAM questionnaire
// so the TRT card's qADAM row resolves to "Received" during standalone dev.
function stubDocuments(emrIntegrationId) {
  return [
    {
      emrDocumentId: `${emrIntegrationId}-doc-1`,
      name: 'Quantitative ADAM Questionnaire 2026-06-20.pdf',
      documentType: 'Other',
      mimeType: 'application/pdf',
      fileExtension: '.pdf',
      documentDate: '2026-06-20',
      status: 'Pending',
      isSigned: false,
      comments: '41/50',
      fileSize: 102400,
      binaryStorageGuid: `${emrIntegrationId}-bin-1`,
      isReadyForDownload: true
    },
    {
      emrDocumentId: `${emrIntegrationId}-doc-2`,
      name: 'Lab Requisition.pdf',
      documentType: 'Other',
      mimeType: 'application/pdf',
      fileExtension: '.pdf',
      documentDate: '2026-05-01',
      status: 'Pending',
      isSigned: false,
      comments: null,
      fileSize: 51200,
      binaryStorageGuid: `${emrIntegrationId}-bin-2`,
      isReadyForDownload: true
    }
  ];
}

// Fake labs, keyed by emrIntegrationId. The testosterone result (received
// 2026-06-01) is newer than the testosterone order (2026-05-01), so the card's
// Labs row resolves to "Received" during standalone dev.
function stubLabs(emrIntegrationId) {
  return {
    orders: [
      {
        emrOrderId: `${emrIntegrationId}-order-1`,
        orderIdentifier: '2604DQ6A',
        status: 'Received',
        labName: 'Quest Diagnostics (QDRT)',
        providerName: 'Dr. Smith',
        orderedDate: '2026-05-01',
        tests: ['TESTOSTERONE, FREE, BIOVAILABLE AND TOTAL, MALES (ADULT), IMMUNOASSAY', 'CBC']
      }
    ],
    results: [
      {
        emrResultId: `${emrIntegrationId}-result-1`,
        placerOrderNumber: 'ZM021014C',
        labName: 'Quest Diagnostics (QDRT)',
        providerName: 'Dr. Smith',
        collectedDate: '2026-05-28',
        receivedDate: '2026-06-01',
        hasAbnormalValue: false,
        allResultsSigned: true,
        resultCount: 8,
        summary: '8 results: TESTOSTERONE, FREE, BIOAVAILABLE AND TOTAL, PDF Report1'
      }
    ]
  };
}

// Fake vitals, keyed by emrIntegrationId. The newest reading (2026-06-18) gives
// the card's Vitals row a recent date and a BP/Pulse tooltip during dev.
function stubVitals(emrIntegrationId) {
  return [
    {
      emrEventId: `${emrIntegrationId}-vitals-1`,
      recordedDate: '2026-06-18',
      eventType: 'Encounter',
      measurements: [
        { name: 'BP', value: '124/78', units: 'mmHg' },
        { name: 'Pulse', value: '72', units: 'bpm' }
      ]
    },
    {
      emrEventId: `${emrIntegrationId}-vitals-2`,
      recordedDate: '2026-03-12',
      eventType: 'Encounter',
      measurements: [
        { name: 'BP', value: '138/86', units: 'mmHg' },
        { name: 'Pulse', value: '78', units: 'bpm' }
      ]
    }
  ];
}

function respond(messageId, payload) {
  setTimeout(() => {
    window.postMessage(
      { source: MESSAGE_SOURCE, messageId, _response: true, payload },
      '*'
    );
  }, RESPONSE_DELAY_MS);
}

export function installEmrStub() {
  if (installed) return;
  installed = true;

  window.addEventListener('message', (event) => {
    const data = event.data;
    if (!data || data.source !== MESSAGE_SOURCE || data._response) return;

    if (data.type === 'STLMC_SEARCH_PATIENTS') {
      const query = (data.payload && data.payload.query ? data.payload.query : '').trim().toLowerCase();
      const patients = query
        ? STUB_EMR_PATIENTS.filter((p) =>
            `${p.firstName} ${p.lastName}`.toLowerCase().includes(query)
          )
        : STUB_EMR_PATIENTS;
      respond(data.messageId, { patients });
      return;
    }

    if (data.type === 'STLMC_GET_MEDICATIONS') {
      const emrIntegrationId = data.payload && data.payload.emrIntegrationId ? data.payload.emrIntegrationId : 'stub';
      respond(data.messageId, { medications: stubMedications(emrIntegrationId) });
      return;
    }

    if (data.type === 'STLMC_GET_DOCUMENTS') {
      const emrIntegrationId = data.payload && data.payload.emrIntegrationId ? data.payload.emrIntegrationId : 'stub';
      respond(data.messageId, { documents: stubDocuments(emrIntegrationId) });
      return;
    }

    if (data.type === 'STLMC_GET_LABS') {
      const emrIntegrationId = data.payload && data.payload.emrIntegrationId ? data.payload.emrIntegrationId : 'stub';
      respond(data.messageId, stubLabs(emrIntegrationId));
      return;
    }

    if (data.type === 'STLMC_GET_VITALS') {
      const emrIntegrationId = data.payload && data.payload.emrIntegrationId ? data.payload.emrIntegrationId : 'stub';
      respond(data.messageId, { vitals: stubVitals(emrIntegrationId) });
      return;
    }
  });
}
